"use client";

import React from "react";
import { ArrowLeft, Calendar, User, Tag, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


declare const wvcClient: any;

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <article className="mx-auto max-w-4xl animate-pulse">
        <div className="mb-8 h-10 w-24 rounded-md bg-muted/50" />
        <div className="mb-4 h-12 w-3/4 rounded-lg bg-muted/50" />
        <div className="mb-8 flex gap-4">
          <div className="h-6 w-32 rounded-md bg-muted/50" />
          <div className="h-6 w-32 rounded-md bg-muted/50" />
        </div>
        <div className="mb-10 aspect-video w-full rounded-2xl bg-muted/50" />
        <div className="space-y-4">
          <div className="h-4 w-full rounded-md bg-muted/50" />
          <div className="h-4 w-full rounded-md bg-muted/50" />
          <div className="h-4 w-5/6 rounded-md bg-muted/50" />
          <div className="h-4 w-full rounded-md bg-muted/50" />
        </div>
      </article>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl text-center py-20">
        <h2 className="text-2xl font-bold font-default text-destructive mb-4">Failed to load article</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Link to="/HomePage">
          <Button variant="outline" className="rounded-md">Return to Home</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl text-center py-20">
        <h2 className="text-3xl font-bold font-default mb-4">Article Not Found</h2>
        <p className="text-muted-foreground mb-8">The junk removal guide you are looking for does not exist or has been moved.</p>
        <Link to="/HomePage">
          <Button className="bg-primary text-primary-foreground rounded-md">Browse All Guides</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl">
      {}
      <div className="mb-10">
        <Link to="/HomePage">
          <Button variant="ghost" className="text-muted-foreground hover:text-primary pl-0 transition-colors">
            <ArrowLeft className="mr-2 size-4" />
            Back to Ottawa Junk Tips
          </Button>
        </Link>
      </div>

      {}
      <header className="mb-10">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {post.categories.map((cat, i) => (
              <span key={cat.id} data-index={i} className="inline-flex items-center rounded-md bg-secondary px-3 py-1 text-xs font-bold text-secondary-foreground uppercase tracking-wider">
                {cat.name}
              </span>
            ))}
          </div>
        )}
        
        <h1 className="mb-6 font-default text-4xl font-bold leading-tight tracking-tight md:text-5xl lg:text-6xl text-foreground">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center gap-y-4 gap-x-6 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-8 border border-border">
                <AvatarImage src={post.author.avatar?.["48"]} alt={post.author.name} />
                <AvatarFallback className="bg-muted text-foreground font-default">{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="font-semibold text-foreground font-default">{post.author.name}</span>
            </div>
          )}
          
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4 text-secondary" />
              <time className="font-default" dateTime={post.date}>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</time>
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <Clock className="size-4 text-secondary" />
            <span className="font-default">4 min read</span>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <figure className="mb-12 overflow-hidden rounded-lg border border-border bg-muted/20 shadow-lg">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="aspect-[21/9] w-full object-cover transition-transform duration-700 hover:scale-105"
          />
        </figure>
      )}

      {}
      <div 
        className="prose prose-lg max-w-none prose-headings:font-default prose-headings:font-bold prose-a:text-secondary hover:prose-a:text-secondary/80 prose-img:rounded-lg mb-16 text-muted-foreground leading-relaxed [&>p]:mb-6 [&>h2]:text-3xl [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-foreground [&>h3]:text-2xl [&>h3]:mt-8 [&>h3]:mb-4 [&>h3]:text-foreground [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>blockquote]:border-l-4 [&>blockquote]:border-secondary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-foreground/80"
        dangerouslySetInnerHTML={{ __html: post.content || '' }} 
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mb-12 flex flex-wrap items-center gap-3">
          <Tag className="size-5 text-muted-foreground" />
          {post.tags.map((tag, i) => (
            <Link key={tag.id} to={tag.link || "#"} data-index={i}>
              <span className="inline-flex items-center rounded-md border border-border bg-card px-3 py-1 text-sm font-default text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12 bg-border" />

      {}
      {post.author && (
        <Card className="mb-16 border-border bg-card shadow-md rounded-lg">
          <CardContent className="flex flex-col sm:flex-row items-center sm:items-start gap-6 pt-6">
            <Avatar className="size-24 border-2 border-secondary/20">
              <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
              <AvatarFallback className="bg-muted text-2xl font-default">{post.author.name.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="text-center sm:text-left">
              <h3 className="font-default text-xl font-bold text-foreground mb-1">Written by {post.author.name}</h3>
              <p className="text-primary text-sm font-bold mb-4 uppercase tracking-wide">Ottawa Disposal Specialist</p>
              <p className="text-muted-foreground text-sm leading-relaxed font-default">
                Dedicated to keeping Ottawa clean and green. With years of experience in local waste management, I share practical advice on decluttering, eco-friendly disposal, and making junk removal simple for homeowners and businesses across the capital.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {}
      <section className="rounded-lg bg-primary border border-primary p-8 text-center sm:text-left sm:p-12 shadow-xl">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-default text-2xl font-bold text-primary-foreground mb-2">Ready to Clear the Clutter?</h3>
            <p className="text-primary-foreground/80 max-w-xl font-default">
              From Kanata to Orleans, we provide fast, friendly, and transparent junk removal services. Get your free quote today.
            </p>
          </div>
          <Link to="/PricingPage">
            <Button size="lg" className="whitespace-nowrap bg-secondary text-secondary-foreground hover:bg-secondary/90 font-bold rounded-md shadow-lg">
              Get a Free Quote
              <ArrowLeft className="ml-2 size-4 rotate-180" />
            </Button>
          </Link>
        </div>
      </section>
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && wvcClient ? wvcClient.getCurrentWPQueryParams() : { post_type: 'post' };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="227588" 
      id="single-post-template-section" 
      className="bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 min-h-screen selection:bg-secondary/30 selection:text-secondary-foreground"
    >
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}